import Image from '@atoms/Image/Image';
import ProfilePictureSizeEnum from '@models/enums/ProfilePictureSizeEnum';
import SizeEnum from '@models/enums/SizeEnum';
import { IAuthor } from '@models/IAuthor';
import AuthorPanel from '@molecules/AuthorPanel/AuthorPanel';
import Carousel from '@molecules/Carousel/Carousel';
import updateDataLayer from '@utils/helpers/gtm';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import { useState } from 'react';
import Swiper from 'swiper';
import { Autoplay, EffectFade } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly author: IAuthor;
  readonly dataTestId?: string;
}

const AuthorCard = ({ author, dataTestId = 'Author-Card' }: Props) => {
  const {
    authorName,
    associatedImages,
    link,
    meta,
    subTitle,
    specialism,
    ambassador,
  } = author;
  const [swiper, setSwiper] = useState<Swiper>();
  const isTouchDevice = useIsTouchDevice();

  return (
    <a
      href={link?.url}
      onClick={() => {
        updateDataLayer({
          event: 'selectContent',
          content_type: `${subTitle}${ambassador ? `, ${specialism}` : ''}`,
          author_name: authorName,
        });
      }}
      data-testid={dataTestId}
    >
      <article className="flex flex-col">
        <div
          className="relative aspect-square"
          onMouseEnter={() => {
            swiper?.autoplay.start();
          }}
          onMouseLeave={() => {
            swiper?.autoplay.stop();
            swiper?.slideTo(0);
          }}
        >
          {
            // This is added in to fix an issue whereby images do not display on IOS devices - NIKDD-2300
            isTouchDevice ? (
              associatedImages?.[0] && (
                <Image
                  image={associatedImages[0]}
                  imageClasses="w-full h-full object-cover object-center absolute right-0 top-0"
                  epiEdit={
                    meta?.editHints?.associatedImages as string | undefined
                  }
                  sizes="(max-width: 768px) 376px, 500px"
                  dataTestId={`${dataTestId}-Image`}
                />
              )
            ) : (
              <Carousel
                additionalModules={[Autoplay, EffectFade]}
                allowTouchMove={false}
                autoplay={{
                  delay: 500,
                }}
                className="h-full w-full"
                effect="fade"
                enabled={
                  !!(associatedImages?.length && associatedImages.length > 1)
                }
                id={authorName}
                loop
                onInit={(s) => {
                  s?.autoplay.stop();
                  setSwiper(s);
                }}
                slidesPerView={1}
                spaceBetween={0}
                speed={500}
              >
                {associatedImages?.map((image, index) => (
                  <SwiperSlide key={image?.desktop?.url}>
                    <Image
                      image={image}
                      imageClasses="w-full h-full object-cover object-center absolute right-0 top-0"
                      epiEdit={
                        meta?.editHints?.associatedImages as string | undefined
                      }
                      sizes="(max-width: 768px) 376px, 500px"
                      dataTestId={`${dataTestId}-Image-${index}`}
                    />
                  </SwiperSlide>
                ))}
              </Carousel>
            )
          }
        </div>

        <div className="py-8 md:py-10">
          <AuthorPanel
            {...author}
            textSize={SizeEnum.Large}
            size={ProfilePictureSizeEnum.Size76}
          />
        </div>
      </article>
    </a>
  );
};

export default AuthorCard;
